import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { remitosApi, notasEnvioApi } from '../api/remitos'
import { zonasApi, asignacionApi } from '../api/zonas'
import { fmtFecha, fmtHoraDe, hoyISO, toISOLocal } from '../utils/fecha'
import { useAuth } from '../context/AuthContext'
import useDirtyGuard from '../hooks/useDirtyGuard'
import { claveBorrador, leerBorrador, guardarBorrador, borrarBorrador } from '../utils/borradorLocal'
import { etiquetaCliente } from '../utils/cliente'
import Boton from '../components/Boton'
import EstadoVigenteBadge from '../components/EstadoVigenteBadge'
import ModalAsignacionRapida from '../components/ModalAsignacionRapida'
import { ChipEntregaAnticipada } from '../components/FrenoEntregaAnticipada'
import { colors, font, text, space, radius } from '../design/tokens'

// Día calendario (YYYY-MM-DD, hora argentina) de una fecha del backend: las
// peladas vienen tal cual; las serializadas con hora se pasan a ART.
function diaISO(v) {
  if (!v) return null
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const dt = new Date(s)
  return isNaN(dt) ? null : toISOLocal(dt)
}

export default function AsignacionPage() {
  const navigate = useNavigate()
  const [remitosT90, setRemitosT90] = useState([])
  const [notasEnvio, setNotasEnvio] = useState([])
  const [zonas, setZonas] = useState([])
  const [loading, setLoading] = useState(true)
  const [seleccionados, setSeleccionados] = useState(new Set())  // 'r-{id}' | 'ne-{id}'
  const [zonaEdits, setZonaEdits] = useState({})
  const [guardandoZona, setGuardandoZona] = useState(null)
  const [msgZona, setMsgZona] = useState({})
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroQ, setFiltroQ] = useState('')
  const [filtroZona, setFiltroZona] = useState('')   // '' | 'sin' | '{id}'
  const [filtroCP, setFiltroCP] = useState('')
  // Cada filtro puede INCLUIR (=) o EXCLUIR (≠) lo elegido: "zona ≠ Interior"
  // = todas menos Interior (Martin, 20 oct 2026).
  const [excluir, setExcluir] = useState({ q: false, tipo: false, zona: false, cp: false })
  const toggleExcluir = (k) => setExcluir(e => ({ ...e, [k]: !e[k] }))
  const [orden, setOrden] = useState({ campo: '', dir: 'asc' })
  const [zonaMasiva, setZonaMasiva] = useState('')      // zona elegida para acción masiva
  const [accionMsg, setAccionMsg] = useState('')        // feedback de acciones masivas
  const [procesando, setProcesando] = useState(false)
  const [showRapida, setShowRapida] = useState(false)
  const [rapidaEnCurso, setRapidaEnCurso] = useState(false)

  // ── Borrador en el navegador (sugerencia #102, Diego Gorfinkiel, 20 oct 2026)
  // Una versión nueva (o un F5) no puede borrar la asignación a medio armar:
  // selección, zonas elegidas sin guardar, zona masiva y filtros se guardan en
  // este navegador (clave por usuario, vence al cambiar de día). Al volver se
  // recupera DESPUÉS de cargar lo disponible del backend, y lo que ya no está
  // (despachado en otra salida, anulado, entregado) se avisa y no se marca.
  const { realUser } = useAuth()
  const claveAsig = claveBorrador('asignacion', realUser?.id)
  const claveRapida = claveBorrador('asignacion_rapida', realUser?.id)
  const [borradorInicial] = useState(() => leerBorrador(claveAsig))
  const recuperacionRef = useRef(false)
  const [listoParaGuardar, setListoParaGuardar] = useState(false)
  const [avisoBorrador, setAvisoBorrador] = useState(null) // {guardado_at, restaurados, faltan[], rapida}

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      // candidatos=1 → unidades despachables con estado_vigente.codigo en
      // (disponible, fallo_sin_reasignar). Así los VARADOS (reagendado/no_entregado
      // sin reasignar, ej. R000061) entran a Asignación y se reasignan directo;
      // el backend auto-libera el viaje fallido al armar la nueva salida.
      const [r90Res, zRes, neRes] = await Promise.all([
        remitosApi.listar({ transporte: 90, candidatos: 1, per_page: 100 }),
        zonasApi.listar(),
        notasEnvioApi.listar({ candidatos: 1, per_page: 100 }),
      ])
      setRemitosT90(r90Res.data.data || [])
      setZonas(Array.isArray(zRes.data) ? zRes.data : (zRes.data.data || []))
      setNotasEnvio(neRes.data.data || [])
    } catch { } finally { setLoading(false) }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const porKey = useMemo(() => new Map([
    ...remitosT90.map(r => [`r-${r.id}`, { numero: r.numero_presea || `#${r.id}`, zonaId: r.zona_id }]),
    ...notasEnvio.map(ne => [`ne-${ne.id}`, { numero: ne.numero_ne, zonaId: ne.zona_id }]),
  ]), [remitosT90, notasEnvio])

  // Zonas elegidas en el desplegable de la fila y todavía no guardadas con OK.
  const zonasPendientes = useMemo(() => Object.entries(zonaEdits).filter(([k, v]) => {
    const it = porKey.get(k)
    return it && v && String(v) !== String(it.zonaId || '')
  }), [zonaEdits, porKey])

  // Recuperar el borrador una sola vez, recién con la lista del backend cargada.
  useEffect(() => {
    if (loading || recuperacionRef.current) return
    recuperacionRef.current = true
    const b = borradorInicial
    const rap = leerBorrador(claveRapida)
    const cantRapida = Array.isArray(rap?.items) ? rap.items.length : 0
    if (b) {
      const seleccion = Array.isArray(b.seleccion) ? b.seleccion : []
      const vigentes = seleccion.filter(x => porKey.has(x.key))
      const faltan = seleccion.filter(x => !porKey.has(x.key)).map(x => x.numero || x.key)
      setSeleccionados(new Set(vigentes.map(x => x.key)))
      setZonaEdits(Object.fromEntries(Object.entries(b.zonaEdits || {}).filter(([k]) => porKey.has(k))))
      if (b.zonaMasiva) setZonaMasiva(b.zonaMasiva)
      const f = b.filtros || {}
      if (f.q) setFiltroQ(f.q)
      if (f.tipo) setFiltroTipo(f.tipo)
      if (f.zona) setFiltroZona(f.zona)
      if (f.cp) setFiltroCP(f.cp)
      if (f.excluir) setExcluir(e => ({ ...e, ...f.excluir }))
      if (f.orden?.campo) setOrden(f.orden)
      setAvisoBorrador({ guardado_at: b.guardado_at, restaurados: vigentes.length, faltan, rapida: cantRapida })
    } else if (cantRapida > 0) {
      setAvisoBorrador({ guardado_at: rap.guardado_at, restaurados: 0, faltan: [], rapida: cantRapida })
    }
    setListoParaGuardar(true)
  }, [loading, borradorInicial, claveRapida, porKey])

  // Guardar a medida que se arma (solo después de recuperar: si no, el primer
  // render vacío pisaría el borrador antes de leerlo).
  useEffect(() => {
    if (!listoParaGuardar) return
    if (seleccionados.size === 0 && zonasPendientes.length === 0) { borrarBorrador(claveAsig); return }
    guardarBorrador(claveAsig, realUser, {
      seleccion: [...seleccionados].map(key => ({ key, numero: porKey.get(key)?.numero || key })),
      zonaEdits: Object.fromEntries(zonasPendientes),
      zonaMasiva,
      filtros: { q: filtroQ, tipo: filtroTipo, zona: filtroZona, cp: filtroCP, excluir, orden },
    })
  }, [listoParaGuardar, seleccionados, zonasPendientes, zonaMasiva, filtroQ, filtroTipo, filtroZona, filtroCP, excluir, orden, claveAsig, realUser, porKey])

  const descartarBorrador = () => {
    borrarBorrador(claveAsig)
    borrarBorrador(claveRapida)
    setSeleccionados(new Set())
    setZonaEdits({})
    setZonaMasiva('')
    setAvisoBorrador(null)
  }

  // Trabajo en curso: F5 / cerrar la pestaña / "Actualizar ahora" del cartel
  // de versión nueva piden confirmación (el borrador igual queda guardado).
  const enCurso = seleccionados.size > 0 || zonasPendientes.length > 0 || rapidaEnCurso
  useDirtyGuard(enCurso, 'Tenés una asignación de salida sin despachar. Queda guardada en este navegador y se recupera al volver a Asignación.')

  const itemsBase = [
    ...((excluir.tipo ? filtroTipo === 'remito' : filtroTipo === 'ne') ? [] : remitosT90.map(r => ({ key: `r-${r.id}`, tipo: 'remito', id: r.id, data: r }))),
    ...((excluir.tipo ? filtroTipo === 'ne' : filtroTipo === 'remito') ? [] : notasEnvio.map(ne => ({ key: `ne-${ne.id}`, tipo: 'ne', id: ne.id, data: ne }))),
  ]

  const hoy = hoyISO()

  // Decorar cada item con campos para filtrar y ordenar
  const itemsDecorados = useMemo(() => itemsBase.map(it => {
    const d = it.data
    const esRemito = it.tipo === 'remito'
    const nombre = esRemito ? d.cliente?.nombre || '' : (d.destinatario?.nombre || '')
    const clienteRemito = it.tipo === 'ne' ? d.remito?.cliente?.nombre || '' : ''
    // T90: si la NV trae direccion_envio (caso Magento), esa manda sobre el
    // billing del cliente; remito libre sin NV usa su dirección propia.
    // NE: siempre el destinatario propio.
    const env = esRemito ? (d.nota_venta?.direccion_envio || d.direccion_envio) : null
    const fuenteDir = esRemito
      ? (env && (env.calle || env.localidad || env.codigo_postal) ? env : d.cliente)
      : d.destinatario
    const calle = fuenteDir?.calle || fuenteDir?.domicilio || ''
    const localidad = fuenteDir?.localidad || ''
    const provincia = fuenteDir?.provincia || ''
    const cp = fuenteDir?.codigo_postal || ''
    const numero = esRemito ? (d.numero_presea || `#${d.id}`) : d.numero_ne
    const fecha = esRemito ? d.fecha_emision : d.remito?.fecha_emision
    const zonaId = d.zona_id || null
    const zonaNombre = d.zona?.nombre || ''
    // Entrega a más de ~50 km (zona Interior) → no corresponde transporte
    // propio, va por Andreani. La regla la dice el backend (Zona), acá solo
    // se muestra. Sugerencia #47.
    const zonaFuera = !!d.zona?.fuera_reparto_propio
    // Entrega para HOY arriba y resaltada; con otra fecha, al fondo; sin
    // fecha, en el medio (no se sabe cuándo va). Martin, 24 sep 2026.
    const diaEntrega = diaISO(d.fecha_entrega_est)
    const entregaHoy = diaEntrega === hoy
    const prioridad = entregaHoy ? 0 : (diaEntrega ? 2 : 1)
    return { ...it, _calc: { nombre, clienteRemito, calle, localidad, provincia, cp, numero, fecha, zonaId, zonaNombre, zonaFuera, entregaHoy, prioridad } }
  }), [itemsBase, hoy])

  // Filtrar
  const items = useMemo(() => {
    const q = filtroQ.trim().toLowerCase()
    const cpQ = filtroCP.trim().toLowerCase()
    let arr = itemsDecorados.filter(it => {
      const c = it._calc
      if (q) {
        const blob = `${c.nombre} ${c.clienteRemito} ${c.calle} ${c.localidad} ${c.provincia} ${c.numero}`.toLowerCase()
        if (blob.includes(q) === excluir.q) return false
      }
      if (cpQ && c.cp.toLowerCase().includes(cpQ) === excluir.cp) return false
      if (filtroZona) {
        const coincide = filtroZona === 'sin' ? !c.zonaId : String(c.zonaId) === String(filtroZona)
        if (coincide === excluir.zona) return false
      }
      return true
    })
    // Ordenar: primero por fecha de entrega (hoy → sin fecha → otra fecha);
    // el orden por columna elegido aplica dentro de cada grupo.
    const dir = orden.dir === 'asc' ? 1 : -1
    arr = [...arr].sort((a, b) => {
      if (a._calc.prioridad !== b._calc.prioridad) return a._calc.prioridad - b._calc.prioridad
      if (!orden.campo) return 0
      const va = (a._calc[orden.campo] || '').toString().toLowerCase()
      const vb = (b._calc[orden.campo] || '').toString().toLowerCase()
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return 0
    })
    return arr
  }, [itemsDecorados, filtroQ, filtroCP, filtroZona, excluir, orden])

  const toggleOrden = (campo) => {
    setOrden(prev => prev.campo === campo
      ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'asc' })
  }
  const ordenIndicador = (campo) => orden.campo === campo ? (orden.dir === 'asc' ? ' ▲' : ' ▼') : ''

  const toggleSeleccion = (key) => {
    setSeleccionados(prev => {
      const s = new Set(prev)
      s.has(key) ? s.delete(key) : s.add(key)
      return s
    })
  }

  const toggleTodos = () => {
    const keys = items.map(i => i.key)
    if (keys.every(k => seleccionados.has(k))) {
      setSeleccionados(prev => { const s = new Set(prev); keys.forEach(k => s.delete(k)); return s })
    } else {
      setSeleccionados(prev => { const s = new Set(prev); keys.forEach(k => s.add(k)); return s })
    }
  }

  const handleGuardarZona = async (key, tipo, id) => {
    const zona_id = zonaEdits[key]
    if (!zona_id) { setMsgZona(p => ({ ...p, [key]: 'Seleccioná una zona.' })); return }
    setGuardandoZona(key)
    try {
      if (tipo === 'remito') {
        await remitosApi.asignarZona(id, { zona_id: parseInt(zona_id) })
      } else {
        await notasEnvioApi.asignarZona(id, parseInt(zona_id))
      }
      setMsgZona(p => ({ ...p, [key]: 'ok' }))
      await cargar()
    } catch (err) {
      setMsgZona(p => ({ ...p, [key]: err.response?.data?.message || 'Error.' }))
    } finally { setGuardandoZona(null) }
  }

  const keysSeleccionados = [...seleccionados]
  const todosSeleccionados = items.length > 0 && items.every(i => seleccionados.has(i.key))

  // Convierte una key seleccionada a {tipo, id} para el backend
  const docDeKey = (k) => k.startsWith('r-')
    ? { tipo: 'remito', id: parseInt(k.slice(2)) }
    : { tipo: 'nota_envio', id: parseInt(k.slice(3)) }

  // Asignar la zona elegida a TODOS los seleccionados de un saque
  const asignarZonaMasiva = async () => {
    if (!zonaMasiva || keysSeleccionados.length === 0) return
    setProcesando(true); setAccionMsg('')
    try {
      const docs = keysSeleccionados.map(docDeKey)
      const { data } = await asignacionApi.zonaMasiva(parseInt(zonaMasiva), docs)
      const zonaNom = zonas.find(z => String(z.id) === String(zonaMasiva))?.nombre || ''
      const aprendidas = data.localidades_aprendidas || 0
      setAccionMsg(
        `✓ Zona "${zonaNom}" asignada a ${(data.remitos || 0) + (data.notas_envio || 0)} documento(s).` +
        (aprendidas > 0 ? ` 🧠 Aprendí ${aprendidas} localidad(es) nueva(s) para la próxima.` : '')
      )
      setZonaMasiva('')
      await cargar()
    } catch (err) {
      setAccionMsg(err.response?.data?.message || 'Error al asignar la zona.')
    } finally { setProcesando(false) }
  }

  // Auto-asignar zonas por localidad/CP a los que NO tienen zona (todos los visibles)
  const autoAsignarZonas = async () => {
    const sinZona = items.filter(i => !i._calc.zonaId)
    if (sinZona.length === 0) { setAccionMsg('Todos los documentos ya tienen zona.'); return }
    setProcesando(true); setAccionMsg('')
    try {
      const docs = sinZona.map(i => ({
        ...docDeKey(i.key),
        localidad: i._calc.localidad,
        codigo_postal: i._calc.cp,
      }))
      const { data } = await asignacionApi.autoZona(docs)
      setAccionMsg(
        `✓ ${data.resueltos} zona(s) detectada(s) automáticamente.` +
        (data.sin_match > 0 ? ` ${data.sin_match} quedaron sin match (asignalas a mano).` : '')
      )
      await cargar()
    } catch (err) {
      setAccionMsg(err.response?.data?.message || 'Error en la auto-asignación.')
    } finally { setProcesando(false) }
  }

  const sinZonaCount = items.filter(i => !i._calc.zonaId).length
  const fueraRepartoCount = items.filter(i => i._calc.zonaFuera).length

  const irAArmarSalida = () => {
    const docs = []
    keysSeleccionados.forEach(k => {
      const item = items.find(i => i.key === k)
      if (!item) return
      if (k.startsWith('r-')) docs.push({
        entidad_tipo: 'remito',
        entidad_id: parseInt(k.slice(2)),
        label: item._calc.numero,
        sub: item._calc.nombre,
        calle: item._calc.calle,
        localidad: item._calc.localidad,
        cp: item._calc.cp,
        zona: item._calc.zonaNombre,
        fuera_reparto: item._calc.zonaFuera,
      })
      else if (k.startsWith('ne-')) docs.push({
        entidad_tipo: 'nota_envio',
        entidad_id: parseInt(k.slice(3)),
        label: item._calc.numero,
        sub: item._calc.nombre,
        calle: item._calc.calle,
        localidad: item._calc.localidad,
        cp: item._calc.cp,
        zona: item._calc.zonaNombre,
        fuera_reparto: item._calc.zonaFuera,
      })
    })
    borrarBorrador(claveAsig)   // la posta la toma Nueva salida (su state sobrevive al F5)
    navigate('/salidas/nueva', { state: { preseleccion: docs } })
  }

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Asignación</h2>
          <p style={st.sub}>Disponibles para armar salida · incluye varados sin reasignar</p>
        </div>
        <div style={st.headerRight}>
          <Boton
            variante="secondary"
            onClick={() => setShowRapida(true)}
            title="Crear una salida escaneando códigos de barras de los remitos/NEs y despachando directo"
          >
            📷 Asignación rápida
          </Boton>
          {items.length > 0 && (
            <Boton
              variante="secondary"
              onClick={autoAsignarZonas}
              disabled={procesando || sinZonaCount === 0}
              title={sinZonaCount > 0
                ? 'Detecta la zona por CP exacto o localidad. Los que no reconoce quedan sin zona para asignar a mano (no adivina).'
                : 'Todos los documentos ya tienen zona'}
            >
              ✨ Auto-asignar zonas{sinZonaCount > 0 ? ` (${sinZonaCount})` : ' ✓'}
            </Boton>
          )}
          {keysSeleccionados.length > 0 && (
            <Boton variante="primary" onClick={irAArmarSalida}>
              Armar salida ({keysSeleccionados.length}) →
            </Boton>
          )}
        </div>
      </div>

      {avisoBorrador && (
        <div style={st.avisoBorrador}>
          <div style={st.avisoBorradorTexto}>
            <strong>Recuperamos tu asignación en curso</strong>
            {avisoBorrador.guardado_at && <> (guardada a las {fmtHoraDe(avisoBorrador.guardado_at)})</>}
            {avisoBorrador.restaurados > 0 && <> · {avisoBorrador.restaurados} documento{avisoBorrador.restaurados !== 1 ? 's' : ''} seleccionado{avisoBorrador.restaurados !== 1 ? 's' : ''}</>}
            {avisoBorrador.rapida > 0 && <> · asignación rápida con {avisoBorrador.rapida} documento{avisoBorrador.rapida !== 1 ? 's' : ''} escaneado{avisoBorrador.rapida !== 1 ? 's' : ''}</>}
            {avisoBorrador.faltan.length > 0 && (
              <div style={st.avisoBorradorFaltan}>
                Ya no están disponibles (se despacharon, anularon o entregaron) y no se marcaron: {avisoBorrador.faltan.join(', ')}
              </div>
            )}
          </div>
          <div style={st.avisoBorradorAcciones}>
            {avisoBorrador.rapida > 0 && (
              <Boton variante="secondary" sm onClick={() => { setShowRapida(true); setAvisoBorrador(a => ({ ...a, rapida: 0 })) }}>
                📷 Retomar asignación rápida
              </Boton>
            )}
            <Boton variante="ghost" sm onClick={descartarBorrador}>Descartar</Boton>
            <Boton variante="ghost" sm onClick={() => setAvisoBorrador(null)} title="Ocultar este aviso (la asignación sigue)">✕</Boton>
          </div>
        </div>
      )}

      {/* Barra de acción masiva — aparece al seleccionar */}
      {keysSeleccionados.length > 0 && (
        <div style={st.bulkBar}>
          <span style={st.bulkCount}>{keysSeleccionados.length} seleccionado{keysSeleccionados.length !== 1 ? 's' : ''}</span>
          <span style={st.bulkSep}>·</span>
          <span style={st.bulkLabel}>Asignar zona:</span>
          <select style={st.bulkSelect} value={zonaMasiva} onChange={e => setZonaMasiva(e.target.value)}>
            <option value="">— Elegir zona —</option>
            {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
          </select>
          <Boton variante="secondary" sm onClick={asignarZonaMasiva} disabled={!zonaMasiva || procesando}>
            {procesando ? 'Aplicando...' : 'Aplicar a todos'}
          </Boton>
          <Boton variante="ghost" sm onClick={() => setSeleccionados(new Set())}>Deseleccionar</Boton>
        </div>
      )}

      {accionMsg && <div style={st.accionMsg}>{accionMsg}</div>}

      {fueraRepartoCount > 0 && (
        <div style={st.avisoAndreani}>
          ⚠ {fueraRepartoCount} documento{fueraRepartoCount !== 1 ? 's' : ''} con entrega a más de ~50 km (zona Interior):
          no corresponde transporte propio — {fueraRepartoCount !== 1 ? 'van' : 'va'} por Andreani.
        </div>
      )}

      {/* Barra de filtros */}
      <div style={st.filtros}>
        <BotonExcluir activo={excluir.q} onClick={() => toggleExcluir('q')} />
        <input
          style={st.fi}
          placeholder="Buscar (nº, cliente, destinatario, calle, localidad)"
          value={filtroQ}
          onChange={e => setFiltroQ(e.target.value)}
        />
        <BotonExcluir activo={excluir.tipo} onClick={() => toggleExcluir('tipo')} />
        <select style={st.fi} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="remito">Solo T90</option>
          <option value="ne">Solo NE</option>
        </select>
        <BotonExcluir activo={excluir.zona} onClick={() => toggleExcluir('zona')} />
        <select style={st.fi} value={filtroZona} onChange={e => setFiltroZona(e.target.value)}>
          <option value="">Todas las zonas</option>
          <option value="sin">Sin zona</option>
          {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
        </select>
        <BotonExcluir activo={excluir.cp} onClick={() => toggleExcluir('cp')} />
        <input
          style={{ ...st.fi, maxWidth: 110 }}
          placeholder="CP"
          value={filtroCP}
          onChange={e => setFiltroCP(e.target.value)}
        />
        <Boton
          variante="ghost"
          onClick={() => { setFiltroQ(''); setFiltroTipo(''); setFiltroZona(''); setFiltroCP(''); setExcluir({ q: false, tipo: false, zona: false, cp: false }); setOrden({ campo: '', dir: 'asc' }) }}
          type="button"
        >Limpiar</Boton>
        <span style={st.contador}>{items.length} resultado{items.length !== 1 ? 's' : ''}</span>
      </div>

      {items.length === 0 ? (
        <div style={st.empty}>No hay items pendientes de asignación.</div>
      ) : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={{ ...st.th, width: 36 }}>
                  <input type="checkbox" checked={todosSeleccionados} onChange={toggleTodos} />
                </th>
                <th style={{ ...st.th, ...st.thSort }} onClick={() => toggleOrden('numero')}>Nº{ordenIndicador('numero')}</th>
                <th style={st.th}>Tipo</th>
                <th style={{ ...st.th, ...st.thSort }} onClick={() => toggleOrden('nombre')}>Destinatario / Cliente{ordenIndicador('nombre')}</th>
                <th style={{ ...st.th, ...st.thSort }} onClick={() => toggleOrden('localidad')}>Dirección{ordenIndicador('localidad')}</th>
                <th style={{ ...st.th, ...st.thSort }} onClick={() => toggleOrden('cp')}>CP{ordenIndicador('cp')}</th>
                <th style={{ ...st.th, ...st.thSort }} onClick={() => toggleOrden('fecha')}>Emisión{ordenIndicador('fecha')}</th>
                <th style={st.th}>Entrega est.</th>
                <th style={{ ...st.th, ...st.thSort }} onClick={() => toggleOrden('zonaNombre')}>Zona{ordenIndicador('zonaNombre')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(({ key, tipo, id, data: d, _calc }) => {
                const seleccionado = seleccionados.has(key)
                const entregaHoy = _calc.entregaHoy
                const fondoFila = seleccionado ? colors.linkBg : (entregaHoy ? colors.okSoft : colors.bg)
                const esRemito = tipo === 'remito'
                const currentZonaId = d.zona_id
                const tieneZona = !!currentZonaId
                const editandoZona = zonaEdits[key] !== undefined || !tieneZona
                const msg = msgZona[key]
                const guardando = guardandoZona === key
                const nombreDisplay = esRemito
                  ? etiquetaCliente(d.cliente)
                  : (d.destinatario?.nombre || '—')
                const subDisplay = tipo === 'ne' ? etiquetaCliente(d.remito?.cliente) : null
                const numero = esRemito ? (d.numero_presea || `#${d.id}`) : d.numero_ne
                const fecha = esRemito ? d.fecha_emision : d.remito?.fecha_emision
                // Entrega estimada: NE T80 trae la suya; T90 la hereda del propio
                // remito (que a su vez la heredó de la NV en el momento del armado).
                const fechaEntregaEst = esRemito ? d.fecha_entrega_est : d.fecha_entrega_est
                const notasEntrega = esRemito ? d.notas_entrega : d.remito?.notas_entrega
                const zonaActual = d.zona?.nombre
                const remitoId = esRemito ? id : d.remito_id

                // Dirección de ENTREGA: T90 prioriza nota_venta.direccion_envio
                // (shipping de Magento) sobre cliente.domicilio (billing); el
                // remito libre sin NV usa su dirección propia.
                // NE: siempre el destinatario propio.
                const envT90 = esRemito ? (d.nota_venta?.direccion_envio || d.direccion_envio) : null
                const fuenteDir = esRemito
                  ? (envT90 && (envT90.calle || envT90.localidad || envT90.codigo_postal) ? envT90 : d.cliente)
                  : d.destinatario
                const calle = fuenteDir?.calle || fuenteDir?.domicilio || ''
                const localidad = fuenteDir?.localidad || ''
                const provincia = fuenteDir?.provincia || ''
                const cp = fuenteDir?.codigo_postal || ''
                const dirPartes = [calle, localidad, provincia].filter(Boolean).join(', ')

                return (
                  <>
                    {/* Fila clickeable → detalle del remito (reemplaza al botón
                        "Ver" del final, que obligaba a scroll lateral). Las
                        celdas interactivas (checkbox, zona) frenan el click. */}
                    <tr
                      key={key}
                      style={{ ...(subDisplay ? st.trDoble : st.tr), background: fondoFila, cursor: 'pointer', ...(entregaHoy ? st.trHoy : null) }}
                      title="Ver el documento"
                      onClick={() => navigate(`/remitos/${remitoId}`)}
                      onMouseEnter={e => { if (!seleccionado) e.currentTarget.style.background = colors.bgSoft }}
                      onMouseLeave={e => { e.currentTarget.style.background = fondoFila }}
                    >
                      <td style={st.td} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={seleccionado} onChange={() => toggleSeleccion(key)} />
                      </td>
                      <td style={st.tdMono}>{numero}</td>
                      <td style={st.td}>
                        <div style={st.tipoCell}>
                          <span style={{ ...st.tipoBadge, ...(tipo === 'remito' ? st.t90 : st.tne) }}>
                            {tipo === 'remito' ? 'T90' : 'NE'}
                          </span>
                          {/* Estado vigente: un varado (Sin reasignar) se ve distinto
                              de un disponible — el operador sabe qué está mandando. */}
                          {d.estado_vigente && <EstadoVigenteBadge estadoVigente={d.estado_vigente} />}
                        </div>
                      </td>
                      <td style={st.td}>
                        <div style={st.principal}>{nombreDisplay}</div>
                        {subDisplay && <div style={st.subtitulo}>{subDisplay}</div>}
                      </td>
                      <td style={{ ...st.tdSec, maxWidth: 240 }}>
                        {dirPartes || <span style={st.placeholder}>—</span>}
                      </td>
                      <td style={st.tdMono}>
                        {cp || <span style={st.placeholder}>—</span>}
                      </td>
                      <td style={st.tdFecha}>{fmtFecha(fecha)}</td>
                      <td style={st.tdFecha} title={notasEntrega || ''}>
                        {fechaEntregaEst
                          ? <>
                              <span style={entregaHoy ? st.fechaHoy : null}>{fmtFecha(fechaEntregaEst)}</span>
                              {entregaHoy && <span style={st.chipHoy}>HOY</span>}
                              {notasEntrega && <span style={{ marginLeft: 4, color: '#7c3aed', fontWeight: 700 }} title={notasEntrega}>✎</span>}
                              <ChipEntregaAnticipada key={`${key}-${d.entrega_anticipada?.estado ?? ''}`} info={d.entrega_anticipada} />
                            </>
                          : <span style={st.placeholder}>—</span>}
                      </td>
                      <td style={st.td} onClick={e => e.stopPropagation()}>
                        {tieneZona && !editandoZona ? (
                          <div style={st.zonaDisplay}>
                            <span style={st.zonaNombre}>
                              {zonaActual}
                              {d.zona?.fuera_reparto_propio && (
                                <span
                                  style={st.chipAndreani}
                                  title="Entrega a más de ~50 km — no corresponde transporte propio, va por Andreani"
                                >⚠ Andreani</span>
                              )}
                            </span>
                            <Boton variante="secondary" sm title="Cambiar zona" onClick={() => setZonaEdits(p => ({ ...p, [key]: currentZonaId }))}>
                              ✏️
                            </Boton>
                          </div>
                        ) : (
                          <div style={st.zonaEdit}>
                            <select
                              style={st.zonaSelect}
                              value={zonaEdits[key] || currentZonaId || ''}
                              onChange={e => setZonaEdits(p => ({ ...p, [key]: e.target.value }))}
                            >
                              <option value="">— Zona —</option>
                              {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                            </select>
                            <Boton
                              variante="primary"
                              sm
                              disabled={guardando}
                              onClick={() => handleGuardarZona(key, tipo, id)}
                            >
                              {guardando ? '...' : 'OK'}
                            </Boton>
                          </div>
                        )}
                        {msg && msg !== 'ok' && <div style={st.msgErr}>{msg}</div>}
                        {msg === 'ok' && <div style={st.msgOk}>Guardado</div>}
                      </td>
                    </tr>
                  </>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {keysSeleccionados.length > 0 && (
        <div style={st.footer}>
          <span style={st.footerCount}>{keysSeleccionados.length} item{keysSeleccionados.length !== 1 ? 's' : ''} seleccionado{keysSeleccionados.length !== 1 ? 's' : ''}</span>
          <Boton variante="primary" onClick={irAArmarSalida}>
            Armar salida →
          </Boton>
        </div>
      )}

      <ModalAsignacionRapida open={showRapida} onClose={() => { setShowRapida(false); cargar() }} onEnCurso={setRapidaEnCurso} />
    </div>
  )
}

// = incluye lo elegido · ≠ lo excluye (todas menos eso). Va pegado al filtro.
function BotonExcluir({ activo, onClick }) {
  return (
    <button type="button" onClick={onClick}
      title={activo ? 'Excluye lo elegido (todos menos eso). Click para incluir.' : 'Incluye lo elegido. Click para excluir (todos menos eso).'}
      style={{
        marginRight: -space.md + 2, width: 28, height: 30, cursor: 'pointer', fontWeight: 700, fontSize: 15,
        border: `1px solid ${activo ? colors.err : colors.textPlh}`, borderRadius: radius.sm,
        background: activo ? colors.errBg : colors.bg, color: activo ? colors.err : colors.textSec,
      }}
    >{activo ? '≠' : '='}</button>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl, flexWrap: 'wrap', gap: space.lg },
  headerRight: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec },
  fi: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', background: colors.bg },

  bulkBar: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap', background: colors.linkBg, border: '1px solid #bfdbfe', borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.md },
  bulkCount: { fontWeight: 600, color: colors.link, fontSize: text.sec },
  bulkSep: { color: colors.textPlh },
  bulkLabel: { fontSize: text.sec, color: '#475569', fontWeight: 500 },
  bulkSelect: { padding: '5px 10px', border: '1px solid #bfdbfe', borderRadius: radius.sm, fontSize: text.sec, fontFamily: 'inherit', minWidth: 140, background: colors.bg },

  avisoBorrador: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.md, flexWrap: 'wrap', background: colors.infoBg, border: `1px solid ${colors.infoBorder}`, color: colors.info, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.md, fontSize: text.sec },
  avisoBorradorTexto: { flex: 1, minWidth: 240, lineHeight: 1.4 },
  avisoBorradorFaltan: { marginTop: 4, color: colors.warn, fontWeight: 500 },
  avisoBorradorAcciones: { display: 'flex', gap: space.sm, alignItems: 'center' },
  accionMsg: { background: colors.okBg, border: '1px solid #86efac', color: colors.ok, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.md, fontSize: text.sec },
  avisoAndreani: { background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.md, fontSize: text.sec, fontWeight: 500 },
  chipAndreani: { marginLeft: 6, fontSize: text.meta, fontWeight: 700, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: radius.xs, padding: '1px 6px', whiteSpace: 'nowrap' },
  empty: { background: colors.bg, borderRadius: radius.md, padding: 40, textAlign: 'center', color: colors.textMeta, border: `1px solid ${colors.border}` },

  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 14px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  trHoy: { boxShadow: `inset 3px 0 0 ${colors.ok}` },
  fechaHoy: { color: colors.ok, fontWeight: 700 },
  chipHoy: { marginLeft: 6, fontSize: text.meta, fontWeight: 700, color: colors.ok, background: colors.okBg, borderRadius: radius.xs, padding: '1px 6px', fontFamily: font.sans },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44 },
  trDoble: { borderBottom: `1px solid ${colors.borderSoft}`, height: 56 },
  td: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.25 },
  tdMono: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdSec: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, lineHeight: 1.25 },
  tdFecha: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, whiteSpace: 'nowrap', lineHeight: 1.25 },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  subtitulo: { fontSize: text.meta, color: colors.textMeta, marginTop: 2 },
  placeholder: { color: colors.textPlh },

  tipoCell: { display: 'flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' },
  tipoBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs, fontSize: text.meta, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' },
  t90: { background: colors.t90Bg, color: colors.t90Text },
  tne: { background: colors.t80Bg, color: colors.t80Text },

  // Nombre a la izquierda, lápiz SIEMPRE al borde derecho: los lapicitos
  // forman una columna alineada en vez de flotar detrás de cada nombre.
  zonaDisplay: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.sm, width: '100%' },
  zonaNombre: { fontSize: text.sec, color: colors.text, fontWeight: 500, lineHeight: 1.2 },
  zonaEdit: { display: 'flex', gap: space.xs, alignItems: 'center' },
  zonaSelect: { padding: '4px 8px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.sec, fontFamily: 'inherit', minWidth: 120, background: colors.bg },
  msgErr: { fontSize: text.meta, color: colors.err, marginTop: 4 },
  msgOk: { fontSize: text.meta, color: colors.ok, marginTop: 4, fontWeight: 500 },
  thSort: { cursor: 'pointer', userSelect: 'none' },

  filtros: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  contador: { marginLeft: 'auto', fontSize: text.sec, color: colors.textSec, fontWeight: 500 },

  footer: { position: 'sticky', bottom: 0, background: colors.bg, borderTop: `1px solid ${colors.border}`, padding: `${space.lg}px ${space.xl}px`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: `0 0 ${radius.md}px ${radius.md}px`, boxShadow: `0 -2px 8px rgba(0,0,0,0.04)`, marginTop: space.md },
  footerCount: { fontWeight: 600, color: colors.text, fontSize: text.body },
}
